import { inject, Injectable } from '@angular/core';
import { User } from '@angular/fire/auth';
import {
  Firestore,
  doc,
  docData,
  setDoc,
} from '@angular/fire/firestore';
import {
  Observable,
  filter,
  switchMap,
  map,
  shareReplay,
  firstValueFrom,
} from 'rxjs';
import { IProfileDb, ProfileModel } from '../models/profile.model';
import { Settings, createDefaultSettings, userDataConverter } from '../models/user-data.codec';
import { Membership, createDefaultMembership } from '../models/membership.codec';
import { AuthService } from '../auth.service';

/**
 * UserService - Handles all user-level data operations
 *
 * Manages the users/{uid} document which contains:
 * - settings: User preferences (units, currency, theme, etc.)
 * - profile: User profile information (name, email, business info, etc.)
 *
 * Benefits of single service for single document:
 * - Single Firestore listener instead of two (more efficient)
 * - Clearer domain model (all user data in one place)
 * - Easier to add new user-level fields
 *
 * Replaces the old ProfileService and SettingsService
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly authService = inject(AuthService);
  private readonly firestore = inject(Firestore);

  public readonly user$ = this.authService.user$;
  public readonly uid$ = this.authService.uid$;

  /**
   * Single source of truth for user document data
   * Shared by both settings$ and profile$ observables
   * Uses Firestore converter for automatic Zod validation
   */
  private readonly userData$ = this.uid$.pipe(
    switchMap((uid) =>
      docData(doc(this.firestore, `users/${uid}`).withConverter(userDataConverter))
    ),
    shareReplay(1),
  );

  /**
   * Observable of user settings
   * Auto-creates default settings if they don't exist
   * Settings validated by Firestore converter with Zod schema
   */
  public readonly settings$: Observable<Settings> = this.userData$.pipe(
    switchMap(async (userData) => {
      const settings = userData?.settings;
      const uid = await firstValueFrom(this.uid$);

      if (!settings || Object.keys(settings).length === 0) {
        // Auto-create default settings
        const defaultSettings = createDefaultSettings();
        await setDoc(
          doc(this.firestore, `users/${uid}`).withConverter(userDataConverter),
          { settings: defaultSettings },
          { merge: true },
        );
        return defaultSettings;
      }

      return settings;
    }),
  );

  /**
   * Observable of user profile
   * Combines Firestore profile data with Auth user data
   * Profile validated by Firestore converter with Zod schema
   */
  public readonly profile$: Observable<ProfileModel> = this.user$.pipe(
    filter((user): user is User => !!user),
    switchMap((user) => {
      return this.userData$.pipe(
        map((userData) => {
          const profile = userData?.profile || {};
          return new ProfileModel({
            ...profile,
            userId: user.uid,
            emailAddress: user.email ?? '',
          });
        }),
      );
    }),
  );

  /**
   * Observable of user membership
   * Returns default membership (type: 'none', isActive: false) if not set
   * Membership validated by Firestore converter with Zod schema
   *
   * Membership is managed by Cloud Functions and read-only from client
   */
  public readonly membership$: Observable<Membership> = this.userData$.pipe(
    map((userData) => userData?.membership ?? createDefaultMembership()),
  );

  /**
   * Update user settings
   */
  public async updateSettings(settings: Partial<Settings>): Promise<void> {
    const uid = await firstValueFrom(this.uid$);
    await setDoc(
      doc(this.firestore, `users/${uid}`).withConverter(userDataConverter),
      { settings },
      { merge: true },
    );
  }

  /**
   * Update user profile
   */
  public async updateProfile(profile: Partial<IProfileDb>): Promise<void> {
    const uid = await firstValueFrom(this.uid$);
    await setDoc(
      doc(this.firestore, `users/${uid}`).withConverter(userDataConverter),
      { profile },
      { merge: true },
    );
  }
}
